import * as Linking from 'expo-linking';
import React, { useCallback } from 'react';
import { ViewStyle } from 'react-native';
import Modal from 'react-native-modal';
import styled from 'styled-components/native';

import { Button } from '~/components/button';
import { Color } from '~/components/color';
import { ModalAccessibilityContainer } from '~/components/modal-accessibility';
import { Spacing } from '~/components/spacing';
import { Text } from '~/components/text';
import { useIntl } from '~/contexts/intl';
import { isHttpUrl, useServiceNotice } from '~/contexts/service-notice';
import { isNative, isWeb } from '~/utils/platform';

/**
 * Dismissible popup showing the current service notice on all platforms (web and
 * native). The title and message come from the server already localized; only the
 * button labels come from the app translations.
 */
export function ServiceNoticeModal() {
    const { notice, noticeVisible, dismissNotice } = useServiceNotice();
    const { formatMessage } = useIntl();

    // Only http(s) links are ever shown or opened (the URL is server-controlled, but
    // this keeps an unexpected scheme from reaching the linking APIs).
    const moreInfoUrl = notice?.moreInfoUrl;
    const canShowMoreInfoUrl = isHttpUrl(moreInfoUrl);

    const handleOpenMoreInfoUrl = useCallback(async () => {
        if (isHttpUrl(moreInfoUrl) && (await Linking.canOpenURL(moreInfoUrl))) {
            await Linking.openURL(moreInfoUrl);
        }
    }, [moreInfoUrl]);

    const handleDismiss = useCallback(async () => {
        await dismissNotice();
    }, [dismissNotice]);

    if (!noticeVisible || !notice) {
        return null;
    }

    return (
        <Modal
            isVisible
            accessibilityViewIsModal
            hasBackdrop
            backdropOpacity={0.6}
            // The card sits in the middle of the screen, so it fades in instead of sliding up
            // from the bottom edge
            animationIn="fadeIn"
            animationOut="fadeOut"
            useNativeDriverForBackdrop={isNative()}
            onBackdropPress={handleDismiss}
            onBackButtonPress={handleDismiss}
        >
            <ModalAccessibilityContainer style={{ flex: 1 }} accessibilityLabel={notice.title}>
                <Container>
                    <Card>
                        <NoticeTitle>{notice.title}</NoticeTitle>
                        <Spacing.Vertical.MEDIUM />
                        {/* The message is the only part that can grow long, so it is the part
                            that scrolls when the card runs out of room (e.g. a short browser
                            window). This keeps the buttons below always reachable. */}
                        <MessageScroll>
                            <Text.PARAGRAPH_1>{notice.message}</Text.PARAGRAPH_1>
                        </MessageScroll>
                        <Spacing.Vertical.MEDIUM />
                        {canShowMoreInfoUrl ? (
                            <NoticeAction>
                                <Button
                                    alignCenter
                                    style={[{ alignSelf: 'stretch' }, NO_WEB_FOCUS_OUTLINE]}
                                    type="secondary"
                                    shape="rounded"
                                    label={formatMessage('service-notice.read-more')}
                                    onPress={handleOpenMoreInfoUrl}
                                />
                                <Spacing.Vertical.SMALL />
                            </NoticeAction>
                        ) : null}
                        <NoticeAction>
                            <Button
                                alignCenter
                                style={{ alignSelf: 'stretch' }}
                                type="primary"
                                shape="rounded"
                                label={formatMessage('service-notice.dismiss')}
                                onPress={handleDismiss}
                            />
                        </NoticeAction>
                    </Card>
                </Container>
            </ModalAccessibilityContainer>
        </Modal>
    );
}

/**
 * Hides the browser's own focus box on web. It is needed on the "read more" button because the
 * dialog moves the keyboard focus to the first button when it opens (see
 * ModalAccessibilityContainer), which made the browser draw a blue rectangle around the button
 * every time the notice appeared. Same approach as the text inputs, see input-text.tsx.
 */
// Note: RN style types don't include outline, thus ignoring
// @ts-ignore
const NO_WEB_FOCUS_OUTLINE: ViewStyle = isWeb() ? { outline: 'none' } : {};

const NoticeTitle = styled(Text.SUBTITLE_1)`
    color: ${Color.SMOOTHIE};
    text-align: center;
`;

const NoticeAction = styled.View`
    align-self: stretch;
`;

/**
 * Widest the card is allowed to get. Without a limit the card follows the window width, which
 * makes the text one very long line on a desktop browser. This is narrower than the app's
 * content widths (see src/hooks/content-widths) on purpose: a dialog reads better narrow and
 * tall than wide and flat.
 */
const CARD_MAX_WIDTH = 480;

const Container = styled.View`
    flex: 1;
    align-items: center;
    justify-content: center;

    /* Keeps the card off the screen edges on a phone, where it is full width */
    padding-horizontal: 16px;
`;

const Card = styled.View`
    background-color: ${Color.BACKGROUND_DEFAULT};
    padding: 16px;
    border-radius: 16px;

    /* 100% keeps the card full width on a phone; the limit only bites on wider screens */
    width: 100%;
    max-width: ${CARD_MAX_WIDTH}px;
    max-height: 100%;

    align-items: center;
`;

const MessageScroll = styled.ScrollView`
    align-self: stretch;
    flex-shrink: 1;
`;
